/* eslint-disable no-param-reassign */
import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { addNewAccessCard, assignAccessCard, assignKeyReference, checkInVisitors, checkOutVisitors, getAccessCards, markCardReturned, securityDashboardRequest, securityDashboardAllTodayRequest, securitySitesRequest, unreturnedCardsAndKeysRequest, visitorsTodayRequest } from './securityDashboardApi';
import { SecurityDashboardState, SecuritySite, SecurityVisitRequest, TemporaryAccessCards, VisitorsDataProps, VisitorUnreturnedCardsAndKeys } from './types';

const emptyRequestState: SecurityVisitRequest = {
    encrypted_id: '',
    id: 0,
    work_description: '',
    visitors_count: 0,
    visitors: [],
    site_address: '',
    status: '',
    dates: [],
    site_name: '',
};

const emptyTemporaryAccessCards = {
    availableKeys: [],
    inUseKeys: [],
    unreturnedKeys: [],
};

const initialState = {
    error: null,
    isFetching: false,
    securityVisitRequests: [emptyRequestState],
    allTodayVisits: [emptyRequestState],
    pagination: {
        currentPage: 1,
        pageSize: 20,
        total: 0,
    },
    visitorsToday: [],
    visitorsTodayPagination: {
        currentPage: 1,
        pageSize: 20,
        total: 0,
    },
    temporaryAccessCards: emptyTemporaryAccessCards,
    unreturnedCardsAndKeys: [],
    unreturnedCardsAndKeysPagination: {
        currentPage: 1,
        pageSize: 20,
        total: 0,
    },
    sites: [],
    selectedSiteIds: [],
    latestListRequestId: null,
    latestTodayRequestId: null,
} as SecurityDashboardState;

// Spinner stays on until the latest of both dashboard requests has answered.
const settleDashboardFetching = (state: SecurityDashboardState) => {
    state.isFetching = state.latestListRequestId !== null || state.latestTodayRequestId !== null;
};

export const securityDashboardSlice = createSlice({
    name: 'securityDashboard',
    initialState,
    reducers: {
        resetPagination: (state: SecurityDashboardState) => {
            state.pagination = {
                currentPage: 1,
                pageSize: 20,
                total: 0,
            };
        },
        setSelectedSiteIds: (state: SecurityDashboardState, action: PayloadAction<number[]>) => {
            state.selectedSiteIds = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(securitySitesRequest.fulfilled, (
                state: SecurityDashboardState,
                action: PayloadAction<{ sites: SecuritySite[] }>,
            ) => {
                state.sites = action.payload?.sites || [];
                // Drop any picked site the officer no longer has. Left alone
                // otherwise, so the dashboard doesn't refetch for nothing.
                const siteIds = new Set(state.sites.map((site) => site.id));
                const stillAllowed = state.selectedSiteIds.filter((id) => siteIds.has(id));
                if (stillAllowed.length !== state.selectedSiteIds.length) {
                    state.selectedSiteIds = stillAllowed;
                }
            });

        // Changing the site filter or the page starts a new request while an
        // older one may still be out. Only the latest of each kind is applied,
        // so a slow older response can't replace what the officer now asked for.
        builder
            .addCase(securityDashboardRequest.pending, (state, action) => {
                state.latestListRequestId = action.meta.requestId;
                state.isFetching = true;
                state.error = null;
            })
            .addCase(securityDashboardRequest.rejected, (state, action) => {
                if (action.meta.requestId !== state.latestListRequestId) {
                    return;
                }
                state.latestListRequestId = null;
                state.error = action.error;
                settleDashboardFetching(state);
            })
            .addCase(securityDashboardRequest.fulfilled, (state, action) => {
                if (action.meta.requestId !== state.latestListRequestId) {
                    return;
                }
                state.latestListRequestId = null;
                settleDashboardFetching(state);
                if (action.payload.visitRequests) {
                    state.securityVisitRequests = action.payload.visitRequests;
                    state.pagination = {
                        currentPage: action.payload.meta.current_page || 1,
                        pageSize: action.payload.meta.per_page || 20,
                        total: action.payload.meta.total || 0,
                    };
                } else {
                    state.securityVisitRequests = [];
                }
            });

        builder
            .addCase(securityDashboardAllTodayRequest.pending, (state, action) => {
                state.latestTodayRequestId = action.meta.requestId;
                state.isFetching = true;
                state.error = null;
            })
            .addCase(securityDashboardAllTodayRequest.rejected, (state, action) => {
                if (action.meta.requestId !== state.latestTodayRequestId) {
                    return;
                }
                state.latestTodayRequestId = null;
                state.error = action.error;
                settleDashboardFetching(state);
            })
            .addCase(securityDashboardAllTodayRequest.fulfilled, (state, action) => {
                if (action.meta.requestId !== state.latestTodayRequestId) {
                    return;
                }
                state.latestTodayRequestId = null;
                settleDashboardFetching(state);
                if (action.payload.visitRequests) {
                    state.allTodayVisits = action.payload.visitRequests;
                } else {
                    state.allTodayVisits = [];
                }
            });

        builder
            .addCase(getAccessCards.fulfilled, (
                state: SecurityDashboardState,
                action: PayloadAction<TemporaryAccessCards>,
            ) => {
                state.isFetching = false;
                if (action.payload) {
                    state.temporaryAccessCards = action.payload;
                } else {
                    state.temporaryAccessCards = emptyTemporaryAccessCards;
                }
            });

        builder
            .addCase(addNewAccessCard.fulfilled, (
                state: SecurityDashboardState,
                action: PayloadAction<TemporaryAccessCards>,
            ) => {
                state.isFetching = false;
                if (action.payload) {
                    state.temporaryAccessCards = action.payload;
                } else {
                    state.temporaryAccessCards = emptyTemporaryAccessCards;
                }
            });

        builder
            .addCase(unreturnedCardsAndKeysRequest.fulfilled, (
                state: SecurityDashboardState,
                action: PayloadAction<{ unreturnedCardsAndKeys: VisitorUnreturnedCardsAndKeys[]; meta: { current_page: number, per_page: number, total: number } }>,
            ) => {
                state.isFetching = false;
                if (action.payload) {
                    state.unreturnedCardsAndKeys = action.payload.unreturnedCardsAndKeys;
                    state.unreturnedCardsAndKeysPagination = {
                        currentPage: action.payload.meta.current_page || 1,
                        pageSize: action.payload.meta.per_page || 20,
                        total: action.payload.meta.total || 0,
                    };
                } else {
                    state.unreturnedCardsAndKeys = [];
                }
            });

        // Fulfilled state for unreturned cards and keys queries
        builder.addMatcher(
            isAnyOf(
                unreturnedCardsAndKeysRequest.fulfilled,
                markCardReturned.fulfilled,
            ),
            (
                state: SecurityDashboardState,
                action: PayloadAction<{ unreturnedCardsAndKeys: VisitorUnreturnedCardsAndKeys[]; meta: { current_page: number, per_page: number, total: number } }>,
            ) => {
                state.isFetching = false;
                if (action.payload) {
                    state.unreturnedCardsAndKeys = action.payload.unreturnedCardsAndKeys;
                } else {
                    state.unreturnedCardsAndKeys = [];
                }
            },
        );

        // Fulfilled state for visitorsTodayRequest
        builder.addMatcher(
            isAnyOf(
                visitorsTodayRequest.fulfilled,
                checkInVisitors.fulfilled,
                checkOutVisitors.fulfilled,
                assignAccessCard.fulfilled,
                assignKeyReference.fulfilled,
            ),
            (
                state: SecurityDashboardState,
                action: PayloadAction<VisitorsDataProps>,
            ) => {
                state.isFetching = false;
                if (action.payload.visitorsToday) {
                    state.visitorsToday = action.payload.visitorsToday;
                    state.visitorsTodayPagination = {
                        currentPage: action.payload.meta.current_page || 1,
                        pageSize: action.payload.meta.per_page || 20,
                        total: action.payload.meta.total || 0,
                    };
                } else {
                    state.visitorsToday = [];
                }
            },
        );

        // Pending state for all requests
        builder.addMatcher(
            isAnyOf(
                visitorsTodayRequest.pending,
                checkInVisitors.pending,
                checkOutVisitors.pending,
                getAccessCards.pending,
                addNewAccessCard.pending,
                assignAccessCard.pending,
                assignKeyReference.pending,
                unreturnedCardsAndKeysRequest.pending,
                markCardReturned.pending,
            ),
            (state: SecurityDashboardState) => {
                state.isFetching = true;
                state.error = null;
            },
        );

        // Error state for all requests
        builder.addMatcher(
            isAnyOf(
                visitorsTodayRequest.rejected,
                checkInVisitors.rejected,
                checkOutVisitors.rejected,
                getAccessCards.rejected,
                addNewAccessCard.rejected,
                assignAccessCard.rejected,
                assignKeyReference.rejected,
                unreturnedCardsAndKeysRequest.rejected,
                markCardReturned.rejected,
            ),
            (state: SecurityDashboardState, action) => {
                state.isFetching = false;
                state.error = action.error;
            },
        );
    },
});

// this is for configureStore
export default securityDashboardSlice.reducer;
export const { resetPagination, setSelectedSiteIds } = securityDashboardSlice.actions;
